import { useCallback, useMemo, useState } from 'react'

import { Badge } from '@wartek-id/badge'
import { Button } from '@wartek-id/button-v1'
import { Icon } from '@wartek-id/icon-v2'
import { Text } from '@wartek-id/text-v1'
import { Dialog, DialogContent, DialogOverlay } from '@wartek-id/dialog-v1'

import { CardKSSchool } from 'app/KinerjaShared/components/CardKSSchool'
import { PENGELOLAAN_KINERJA_KS_PENILAI_PATH } from 'app/KinerjaShared/constants/path'
import { PANDUAN_PENGELOLAAN_URL } from 'app/KinerjaShared/utils/panduan'
import { PltKepalaSekolahConfirmationDialog } from 'app/KinerjaShared/components/PltKepalaSekolahConfirmationDialog'
import { assetUrl } from 'utils/url'

import type { PelaksanaanSchool } from 'types/kinerja-pelaksanaan'

type PeriksaPerencanaanPegawaiCardProps = {
  schools: PelaksanaanSchool[]
  pegawaiLabel?: string
  badgeText?: string
  kepalaSekolahLabel?: string
  resolveNextPath: (school: PelaksanaanSchool) => string
}

export function PeriksaPerencanaanPegawaiCard({
  schools,
  pegawaiLabel = 'Guru',
  badgeText,
  kepalaSekolahLabel = 'Kepala Sekolah',
  resolveNextPath,
}: PeriksaPerencanaanPegawaiCardProps) {
  // -----------------------------------------
  // Resolve schools in Perencanaan phase
  // -----------------------------------------
  // Schools that has at least one pegawai in the Perencanaan phase
  const unfinishedPerencanaanSchools = useMemo(
    () =>
      schools.filter((school) =>
        ['EMPTY', 'NOT_COMPLETED'].includes(school.perencanaanReadyState)
      ),
    [schools]
  )
  const hasUnfinishedPerencanaanSchools =
    unfinishedPerencanaanSchools.length > 0

  // Whether every pegawai in each schools is still in the Perencanaan phase
  const isAllSchoolsInPerencanaanPhase = useMemo(
    () => schools.every((school) => school.perencanaanReadyState === 'EMPTY'),
    [schools]
  )

  // -----------------------------------------
  // Handle select schools
  // -----------------------------------------
  const [showSelectSchoolsDialog, setShowSelectSchoolsDialog] = useState(false)

  const handleShowSelectSchoolsDialog = () => {
    setShowSelectSchoolsDialog(true)
  }

  const handleCloseSelectSchoolsDialog = () => {
    setShowSelectSchoolsDialog(false)
  }

  // -----------------------------------------
  // Handle Plt confirmation dialog
  // -----------------------------------------
  const [showDialogPltConfirmation, setShowDialogPltConfirmation] =
    useState(false)

  const handleShowDialogPltConfirmation = () => {
    setShowDialogPltConfirmation(true)
  }

  // -----------------------------------------
  // Render schools
  // -----------------------------------------
  const renderSchools = useCallback(() => {
    return unfinishedPerencanaanSchools.map((school) => {
      const nextPath = resolveNextPath(school)

      return (
        <CardKSSchool
          key={school.npsn}
          nextText="Periksa"
          nextPath={nextPath}
          confirmationPath={
            school.statusSatuanPendidikan === 'SWASTA'
              ? `${PENGELOLAAN_KINERJA_KS_PENILAI_PATH}/konfirmasi-swasta/${school.npsn}?successPath=${nextPath}`
              : undefined
          }
          onConfirmClick={
            school.statusSatuanPendidikan === 'NEGERI'
              ? handleShowDialogPltConfirmation
              : undefined
          }
          confirmationStatus={school.confirmationStatus}
          schoolName={school.schoolName}
          schoolStatus={school.statusSatuanPendidikan}
          schoolRole={school.role}
          npsn={school.npsn}
        />
      )
    })
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [unfinishedPerencanaanSchools])

  // -----------------------------------------
  // Resolve render flag
  // -----------------------------------------
  // If every pegawai in each schools has finished the Perencanaan phase, render nothing
  if (!hasUnfinishedPerencanaanSchools) {
    return null
  }

  // If all pegawai in each schools is still in the Perencanaan phase
  // render Periksa Perencanaan card
  if (isAllSchoolsInPerencanaanPhase) {
    return (
      <section className="shadow-card rounded-lg p-6 bg-white">
        <div className="flex items-center gap-6">
          <img
            alt=""
            src={assetUrl('/images/img-typing-monitor.png')}
            width={84}
            height={84}
            className="flex-shrink-0"
          />
          <div>
            <div className="flex items-center space-x-1">
              <Text variant="heading-lg" as="h2" className="mb-1">
                Periksa Perencanaan Kinerja
              </Text>
              {badgeText && (
                <Badge className="uppercase whitespace-nowrap">
                  {badgeText}
                </Badge>
              )}
            </div>
            <Text color="subdued">
              Cek Perencanaan yang sudah diajukan oleh {pegawaiLabel}.
            </Text>
          </div>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-2 2xl:grid-cols-3 gap-4 mt-6">
          {renderSchools()}
        </div>

        <PltKepalaSekolahConfirmationDialog
          isOpen={showDialogPltConfirmation}
          schools={schools}
          kepalaSekolahLabel={kepalaSekolahLabel}
          onClose={() => setShowDialogPltConfirmation(false)}
        />

        <div className="rounded bg-cyan-5 p-3 mt-6 flex items-center">
          <Icon color="informational" className="mr-1">
            error
          </Icon>
          Jika satuan pendidikan tidak sesuai, mohon kunjungi&nbsp;
          <a
            className="!text-blue-70 underline"
            href={PANDUAN_PENGELOLAAN_URL}
            target="_blank"
            rel="noreferrer"
          >
            Pusat Bantuan
          </a>
          .
        </div>
      </section>
    )
  }

  // Otherwise, some pegawai have not finished the Perencanaan phase but some other have
  return (
    <>
      <section className="shadow-card rounded-lg p-6 bg-white">
        <div className="flex gap-6">
          <div className="flex-1 flex flex-col gap-1 justify-center">
            <div className="flex items-center gap-2">
              <Text variant="heading-md" as="h2">
                Ada Perencanaan {pegawaiLabel} yang belum selesai
              </Text>
            </div>
            <Text color="subdued">
              Sebelum lanjut ke tahap Penilaian, selesaikan tahap Perencanaan{' '}
              {pegawaiLabel} terlebih dahulu.
            </Text>
          </div>
          <div className="flex flex-col justify-center">
            <Button
              color="white"
              as="a"
              onClick={handleShowSelectSchoolsDialog}
            >
              Ke Perencanaan {pegawaiLabel} <Icon>chevron_right</Icon>
            </Button>
          </div>
        </div>
      </section>
      <Dialog
        isOpen={showSelectSchoolsDialog}
        onClose={() => setShowSelectSchoolsDialog(false)}
        customSize={484}
        autoFocus={false}
      >
        <DialogOverlay />
        <DialogContent
          className="p-6 m-auto self-center"
          aria-labelledby="select-schools-title"
        >
          <div className="flex justify-between items-center">
            <Text variant="heading-md" as="h2" id="select-schools-title">
              Pilih satuan pendidikan
            </Text>
            <Button
              color="white"
              iconOnly
              size="sm"
              variant="ghost"
              className="!outline-none"
              onClick={handleCloseSelectSchoolsDialog}
            >
              <Icon color="inherit" fontSize="lg">
                close
              </Icon>
            </Button>
          </div>
          <div className="space-y-4 mt-6">{renderSchools()}</div>
        </DialogContent>
      </Dialog>
    </>
  )
}
